"use client";

import React, { useMemo, useState } from "react";
import { TabBar } from "./tabBar";
import { ArticleFeedItem } from "./articleFeedItem";
import { FEED_TABS } from "./constants";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { LinkButton } from "components/simpleButton/linkButton";
import type { ArticleFeedProps, TabConfig } from "./types";
export const ArticleFeed: React.FC<ArticleFeedProps> = ({
  popularArticles,
  freeToReadArticles,
  collaborationArticles,
  tabLabels,
  tabOrder,
  ctaText,
  ctaLink,
  secondTabCta,
  thirdTabCta,
}) => {
  const tabs: TabConfig[] = useMemo(() => {
    const fallbackOrder = {
      [FEED_TABS.POPULAR]: 0,
      [FEED_TABS.FREE_TO_READ]: 1,
      [FEED_TABS.COLLABORATIONS]: 2,
    };

    return [
      {
        id: FEED_TABS.POPULAR,
        label: tabLabels[FEED_TABS.POPULAR],
      },
      {
        id: FEED_TABS.FREE_TO_READ,
        label: tabLabels[FEED_TABS.FREE_TO_READ],
      },
      {
        id: FEED_TABS.COLLABORATIONS,
        label: tabLabels[FEED_TABS.COLLABORATIONS],
      },
    ].sort((firstTab, secondTab) => {
      const firstOrder = tabOrder?.[firstTab.id];
      const secondOrder = tabOrder?.[secondTab.id];

      const normalizedFirstOrder =
        typeof firstOrder === "number" ? firstOrder : Number.MAX_SAFE_INTEGER;
      const normalizedSecondOrder =
        typeof secondOrder === "number" ? secondOrder : Number.MAX_SAFE_INTEGER;

      if (normalizedFirstOrder !== normalizedSecondOrder) {
        return normalizedFirstOrder - normalizedSecondOrder;
      }

      return fallbackOrder[firstTab.id] - fallbackOrder[secondTab.id];
    });
  }, [tabLabels, tabOrder]);

  const [activeTab, setActiveTab] = useState<
    (typeof FEED_TABS)[keyof typeof FEED_TABS]
  >(tabs[0]?.id ?? FEED_TABS.POPULAR);
  const { track } = useMixpanelTracking();

  const handleTabChange = async (
    tabId: (typeof FEED_TABS)[keyof typeof FEED_TABS]
  ) => {
    setActiveTab(tabId);
    await track("Article feed tab switch", {
      "Tab name": tabId,
      Component: "FeedsSection",
    });
  };

  const tabArticles = {
    [FEED_TABS.POPULAR]: popularArticles,
    [FEED_TABS.FREE_TO_READ]: freeToReadArticles,
    [FEED_TABS.COLLABORATIONS]: collaborationArticles,
  };

  const currentArticles = tabArticles[activeTab];

  const getCurrentCta = () => {
    if (
      activeTab === FEED_TABS.FREE_TO_READ &&
      secondTabCta?.text &&
      secondTabCta?.link
    ) {
      return { text: secondTabCta.text, link: secondTabCta.link };
    }
    if (
      activeTab === FEED_TABS.COLLABORATIONS &&
      thirdTabCta?.text &&
      thirdTabCta?.link
    ) {
      return { text: thirdTabCta.text, link: thirdTabCta.link };
    }
    if (activeTab === FEED_TABS.POPULAR && ctaText && ctaLink) {
      return { text: ctaText, link: ctaLink };
    }
    return null;
  };

  const currentCta = getCurrentCta();

  return (
    <div className="w-full md:mx-auto md:max-w-[800px]">
      <TabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

      <div className="w-full">
        <div
          role="tabpanel"
          id={`panel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          className="flex flex-col"
        >
          {currentArticles && currentArticles.length > 0 && (
            <>
              {currentArticles.map((article, index) => (
                <ArticleFeedItem
                  key={article.id || article.url}
                  article={article}
                  position={index}
                />
              ))}
            </>
          )}
        </div>

        {currentCta && (
          <div className="mt-4 flex w-full justify-start">
            <LinkButton
              href={currentCta.link}
              variant="secondary"
              accent="neutral"
              size="md"
            >
              {currentCta.text}
            </LinkButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleFeed;
